"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { shortExpansion } from "@/lib/tool-kit/arithmetic/division-view";
import { decimalExpansion } from "@/lib/tool-kit/arithmetic/expansion";
import {
  type Evaluation,
  type EvaluationStep,
  evaluateSteps,
  type ImplicitMode,
  resultForms,
  type Stage,
} from "@/lib/tool-kit/arithmetic/order-of-operations";
import {
  formatRationalNice,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const ORDER_OF_OPERATIONS_CALCULATOR_STRINGS = {
  expressionLabel: "Expression",
  expressionHint:
    "Use + − × ÷ ^ and brackets ( ) [ ] { }. Write × as * and ÷ as / if you like; use a decimal point, not a comma.",
  mnemonicLabel: "Mnemonic to label the steps",
  mnemonics: {
    pemdas: "PEMDAS",
    bodmas: "BODMAS",
    bidmas: "BIDMAS",
    bedmas: "BEDMAS",
  },
  bracketWord: {
    pemdas: "parentheses",
    bodmas: "brackets",
    bidmas: "brackets",
    bedmas: "brackets",
  },
  stageBrackets: {
    pemdas: "Parentheses",
    bodmas: "Brackets",
    bidmas: "Brackets",
    bedmas: "Brackets",
  },
  stageExponents: {
    pemdas: "Exponents",
    bodmas: "Orders",
    bidmas: "Indices",
    bedmas: "Exponents",
  },
  stageNegate: "Minus sign",
  stageImplicit: "Implicit multiplication",
  stageMulDiv: "Multiplication and division, left to right",
  stageAddSub: "Addition and subtraction, left to right",
  mnemonicNote:
    "PEMDAS, BODMAS, BIDMAS and BEDMAS all rank the operations the same way. Multiplication and division share a rank, as do addition and subtraction, so each pair is done left to right.",
  implicitLabel: "Implicit multiplication, as in 2(3 + 4)",
  implicitSame: "Same rank as × and ÷ (left to right)",
  implicitTighter: "Binds tighter than ÷ (8 ÷ 2(2 + 2) = 1)",
  examplesLabel: "Try an example",
  examples: ["8 ÷ 2 × 4", "−2^2", "2^3^2", "8 ÷ 2(2 + 2)", "1 ÷ 3 × 3"],
  results: "Results",
  answer: "Answer",
  asFraction: "As a fraction",
  asMixed: "As a mixed number",
  asDecimal: "As a decimal",
  copy: "Copy answer",
  copied: "Copied!",
  readAs: "Read as: {expression}",
  stepsHeading: "Step by step",
  insideStage: "{brackets}, then {stage}",
  bracketsDropped: "the {brackets} now hold one number, so they are removed",
  bracketsOnly: "A {brackets} pair around a single number is removed.",
  alreadyDone:
    "There is nothing to calculate: the expression is already a single number.",
  noteImplicit:
    "Writing a number straight in front of a bracket, as in 8 ÷ 2(2 + 2), can be read two ways. Treated as ordinary multiplication, left to right, it gives {same}; if it ranks above division, it gives {tighter}. Add brackets, as in 8 ÷ (2(2 + 2)), to remove the doubt.",
  noteTighter:
    "Implicit multiplication is ranked above ÷ here, the way many physics and algebra texts read 1/2x.",
  noteMinus:
    "A minus sign in front of a power applies after the power: −2^2 = −(2^2) = −4. Some spreadsheets, Excel among them, read it as (−2)^2 = 4. Type (−2)^2 for the second.",
  noteChain:
    "Stacked powers are worked from the top down: 2^3^2 = 2^(3^2) = 512. Excel and a few other programs read a caret chain from the left, as (2^3)^2 = 64. Add brackets to be explicit.",
  enterExpression: "Enter an expression to see it worked step by step.",
  errors: {
    empty: "Enter an expression to see it worked step by step.",
    "too-long": "Please keep the expression under 200 characters.",
    "bad-character":
      "“{token}” is not allowed here. Use numbers, + − × ÷ ^ and brackets.",
    comma:
      "Use a decimal point instead of a comma, and write 1000 rather than 1,000.",
    "missing-operand":
      "An operator or bracket is missing a number next to it. Check for a doubled operator or an empty pair of brackets.",
    "missing-operator":
      "Two numbers sit side by side. Put an operator between them, or join them with a bracket as in 2(3).",
    unbalanced:
      "The brackets do not pair up: one is opened and not closed, or the reverse.",
    mismatched:
      "A bracket is closed with a different kind of bracket than the one that opened it.",
    "divide-by-zero": "The expression divides by zero, which is undefined.",
    "zero-negative-power":
      "Zero cannot be raised to a negative power: it would mean dividing by zero.",
    "not-rational":
      "This power is not a rational number (or not real), so it cannot be kept exact. Use whole-number exponents or roots with an exact answer, such as 4^0.5.",
    "too-large": "A number in the working became too large to show exactly.",
    "too-many-steps":
      "That expression needs more than 120 steps. Try a shorter one.",
  },
} as const;

type Strings = typeof ORDER_OF_OPERATIONS_CALCULATOR_STRINGS;
type Mnemonic = keyof Strings["mnemonics"];

const MNEMONICS: readonly Mnemonic[] = ["pemdas", "bodmas", "bidmas", "bedmas"];

function stageLabel(
  stage: Stage,
  mnemonic: Mnemonic,
  strings: Strings
): string {
  switch (stage) {
    case "brackets":
      return strings.stageBrackets[mnemonic];
    case "exponents":
      return strings.stageExponents[mnemonic];
    case "negate":
      return strings.stageNegate;
    case "implicit":
      return strings.stageImplicit;
    case "muldiv":
      return strings.stageMulDiv;
    default:
      return strings.stageAddSub;
  }
}

function StepItem({
  step,
  mnemonic,
  strings,
}: {
  step: EvaluationStep;
  mnemonic: Mnemonic;
  strings: Strings;
}) {
  const brackets = strings.bracketWord[mnemonic];
  const stage = stageLabel(step.stage, mnemonic, strings);
  const label =
    step.inBrackets && step.operation
      ? t(strings.insideStage, {
          brackets: strings.stageBrackets[mnemonic],
          stage: stage.charAt(0).toLowerCase() + stage.slice(1),
        })
      : stage;
  const detail = step.operation || t(strings.bracketsOnly, { brackets });
  const dropped =
    step.bracketsDropped && step.operation
      ? ` (${t(strings.bracketsDropped, { brackets })})`
      : "";
  return (
    <li className="space-y-1">
      <p className="text-foreground text-sm">
        <span className="font-medium">{label}</span>: {detail}
        {dropped}
      </p>
      <p className="break-words font-mono text-muted-foreground text-sm">
        {step.after}
      </p>
    </li>
  );
}

function decimalOf(value: Rational): string {
  const nice = formatRationalNice(value, ".", 12);
  if (!nice.includes("/")) {
    return nice;
  }
  const expansion = decimalExpansion(value.num, value.den);
  return shortExpansion(expansion);
}

function Success({
  evaluation,
  mnemonic,
  implicit,
  source,
  strings,
}: {
  evaluation: Extract<Evaluation, { ok: true }>;
  mnemonic: Mnemonic;
  implicit: ImplicitMode;
  source: string;
  strings: Strings;
}) {
  const forms = resultForms(evaluation.result);
  const decimal = decimalOf(evaluation.result);
  const answer = forms.integer ? forms.fraction : decimal;
  const other = evaluateSteps(source, implicit === "same" ? "tighter" : "same");
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.answer} value={answer} />
        {forms.integer ? null : (
          <StatCard label={strings.asFraction} value={forms.fraction} />
        )}
        {forms.mixed ? (
          <StatCard label={strings.asMixed} value={forms.mixed} />
        ) : null}
        {!forms.integer && decimal !== answer ? (
          <StatCard label={strings.asDecimal} value={decimal} />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={`${evaluation.read} = ${forms.integer ? forms.fraction : `${forms.fraction} = ${decimal}`}`}
      />
      <p className="break-words font-mono text-muted-foreground text-sm">
        {t(strings.readAs, { expression: evaluation.read })}
      </p>
      {evaluation.flags.ambiguousImplicit && other.ok ? (
        <Notice>
          {t(strings.noteImplicit, {
            same: decimalOf(
              implicit === "same" ? evaluation.result : other.result
            ),
            tighter: decimalOf(
              implicit === "same" ? other.result : evaluation.result
            ),
          })}
        </Notice>
      ) : null}
      {implicit === "tighter" ? <Notice>{strings.noteTighter}</Notice> : null}
      {evaluation.flags.minusBeforePower ? (
        <Notice>{strings.noteMinus}</Notice>
      ) : null}
      {evaluation.flags.chainedPowers ? (
        <Notice>{strings.noteChain}</Notice>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      {evaluation.steps.length === 0 ? (
        <p className="text-muted-foreground text-sm">{strings.alreadyDone}</p>
      ) : (
        <ol className="list-decimal space-y-3 pl-6">
          {evaluation.steps.map((step) => (
            // Every step shortens the expression, so the text after it is unique.
            <StepItem
              key={step.after}
              mnemonic={mnemonic}
              step={step}
              strings={strings}
            />
          ))}
        </ol>
      )}
      <p className="text-muted-foreground text-sm">{strings.mnemonicNote}</p>
    </>
  );
}

export default function OrderOfOperationsCalculatorWidget() {
  const strings = useToolUi(ORDER_OF_OPERATIONS_CALCULATOR_STRINGS);
  const [source, setSource] = useState("2 + 3 × (4 − 1)^2 ÷ 9 − 1");
  const [mnemonic, setMnemonic] = useState<Mnemonic>("pemdas");
  const [implicit, setImplicit] = useState<ImplicitMode>("same");
  const evaluation = useMemo(
    () => (source.trim() === "" ? null : evaluateSteps(source, implicit)),
    [source, implicit]
  );
  let body = <Notice>{strings.enterExpression}</Notice>;
  if (evaluation && !evaluation.ok) {
    const template = strings.errors[evaluation.error];
    body = (
      <ErrorNotice>{t(template, { token: evaluation.token })}</ErrorNotice>
    );
  } else if (evaluation?.ok) {
    body = (
      <Success
        evaluation={evaluation}
        implicit={implicit}
        mnemonic={mnemonic}
        source={source}
        strings={strings}
      />
    );
  }
  return (
    <>
      <TextField
        hint={strings.expressionHint}
        id="oo-expression"
        label={strings.expressionLabel}
        mono
        onChange={setSource}
        value={source}
      />
      <div className="space-y-2">
        <p className={LABEL}>{strings.examplesLabel}</p>
        <div className="flex flex-wrap gap-2">
          {strings.examples.map((example) => (
            <button
              className={BUTTON_SMALL}
              key={example}
              onClick={() => setSource(example)}
              type="button"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="oo-mnemonic"
          label={strings.mnemonicLabel}
          onChange={setMnemonic}
          options={MNEMONICS.map((m) => ({
            value: m,
            label: strings.mnemonics[m],
          }))}
          value={mnemonic}
        />
        <SelectField
          id="oo-implicit"
          label={strings.implicitLabel}
          onChange={setImplicit}
          options={[
            { value: "same", label: strings.implicitSame },
            { value: "tighter", label: strings.implicitTighter },
          ]}
          value={implicit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
